/**
 * CSS-in-JS styles for Proctoring SDK UI
 * Uses CSS variables for theming support
 */

const themes = {
  dark: {
    "--proctor-bg": "rgba(17, 24, 39, 0.95)",
    "--proctor-bg-solid": "#111827",
    "--proctor-text": "#f9fafb",
    "--proctor-text-muted": "#9ca3af",
    "--proctor-border": "rgba(75, 85, 99, 0.5)",
    "--proctor-success": "#10b981",
    "--proctor-warning": "#f59e0b",
    "--proctor-error": "#ef4444",
    "--proctor-info": "#3b82f6",
    "--proctor-shadow": "0 10px 40px rgba(0, 0, 0, 0.4)",
  },
  light: {
    "--proctor-bg": "rgba(255, 255, 255, 0.95)",
    "--proctor-bg-solid": "#ffffff",
    "--proctor-text": "#111827",
    "--proctor-text-muted": "#6b7280",
    "--proctor-border": "rgba(209, 213, 219, 0.8)",
    "--proctor-success": "#059669",
    "--proctor-warning": "#d97706",
    "--proctor-error": "#dc2626",
    "--proctor-info": "#2563eb",
    "--proctor-shadow": "0 10px 40px rgba(0, 0, 0, 0.15)",
  },
};

const baseStyles = `
    * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    /* Scrollbar Styling */
    *::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    *::-webkit-scrollbar-track {
        background: transparent;
    }

    *::-webkit-scrollbar-thumb {
        background: var(--proctor-border);
        border-radius: 3px;
    }

    *::-webkit-scrollbar-thumb:hover {
        background: var(--proctor-text-muted);
    }

    :host {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        color: var(--proctor-text);
        pointer-events: none;
    }

    /* All UI elements need pointer-events */
    .status-indicator,
    .notifications-container,
    .notification,
    .camera-preview,
    .permission-overlay {
        pointer-events: auto;
    }

    /* Animations */
    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateX(100%);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    @keyframes slideOut {
        from {
            opacity: 1;
            transform: translateX(0);
        }
        to {
            opacity: 0;
            transform: translateX(100%);
        }
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
    }

    @keyframes scaleIn {
        from {
            opacity: 0;
            transform: scale(0.9);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    /* Status Indicator */
    .status-indicator {
        position: fixed;
        z-index: 999998;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 14px;
        background: var(--proctor-bg);
        border: 1px solid var(--proctor-border);
        border-radius: 24px;
        box-shadow: var(--proctor-shadow);
        backdrop-filter: blur(10px);
        animation: scaleIn 0.3s ease-out;
        cursor: pointer;
        user-select: none;
    }

    .status-indicator.minimized {
        padding: 8px;
        border-radius: 50%;
    }

    .status-indicator.minimized .status-text,
    .status-indicator.minimized .status-timer {
        display: none;
    }

    .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--proctor-error);
        animation: pulse 2s infinite;
    }

    .status-dot.inactive {
        background: var(--proctor-text-muted);
        animation: none;
    }

    .status-text {
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
    }

    .status-timer {
        font-size: 12px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.3px;
        padding-left: 8px;
        border-left: 1px solid var(--proctor-border);
    }

    /* Notification Toast */
    .notifications-container {
        position: fixed;
        z-index: 999999;
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: 360px;
        width: 100%;
    }

    .notification {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 16px;
        background: var(--proctor-bg);
        border: 1px solid var(--proctor-border);
        border-radius: 12px;
        box-shadow: var(--proctor-shadow);
        backdrop-filter: blur(10px);
        animation: slideIn 0.3s ease-out;
    }

    .notification.exiting {
        animation: slideOut 0.3s ease-in forwards;
    }

    .notification-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
    }

    .notification.warning .notification-icon {
        color: var(--proctor-warning);
    }

    .notification.error .notification-icon {
        color: var(--proctor-error);
    }

    .notification.info .notification-icon {
        color: var(--proctor-info);
    }

    .notification.success .notification-icon {
        color: var(--proctor-success);
    }

    .notification-content {
        flex: 1;
        min-width: 0;
    }

    .notification-title {
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 2px;
    }

    .notification-message {
        font-size: 13px;
        color: var(--proctor-text-muted);
    }

    .notification-close {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        padding: 0;
        background: none;
        border: none;
        color: var(--proctor-text-muted);
        cursor: pointer;
        opacity: 0.6;
        transition: opacity 0.2s;
    }

    .notification-close:hover {
        opacity: 1;
    }

    /* Camera Preview */
    .camera-preview {
        position: fixed;
        z-index: 999997;
        width: 150px;
        height: 150px;
        background: var(--proctor-bg-solid);
        border: 3px solid var(--proctor-border);
        border-radius: 50%;
        box-shadow: var(--proctor-shadow);
        overflow: hidden;
        animation: scaleIn 0.3s ease-out;
        cursor: move;
    }



    .camera-preview video {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        background: #000;
    }

    /* Permission Guide Overlay */
    .permission-overlay {
        position: fixed;
        inset: 0;
        z-index: 1000000;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.75);
        backdrop-filter: blur(4px);
        animation: fadeIn 0.3s ease-out;
    }

    .permission-card {
        width: 90%;
        max-width: 420px;
        background: var(--proctor-bg-solid);
        border-radius: 16px;
        box-shadow: var(--proctor-shadow);
        overflow: hidden;
        animation: scaleIn 0.3s ease-out;
    }

    .permission-header {
        padding: 24px 24px 0;
        text-align: center;
    }

    .permission-icon {
        width: 64px;
        height: 64px;
        margin: 0 auto 16px;
        padding: 16px;
        background: var(--proctor-bg);
        border-radius: 16px;
        color: var(--proctor-info);
    }

    .permission-title {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 8px;
    }

    .permission-description {
        font-size: 14px;
        color: var(--proctor-text-muted);
    }

    .permission-steps {
        padding: 24px;
    }

    .permission-step {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 0;
    }

    .permission-step:not(:last-child) {
        border-bottom: 1px solid var(--proctor-border);
    }

    .permission-step-number {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--proctor-info);
        color: white;
        font-size: 12px;
        font-weight: 600;
        border-radius: 50%;
    }

    .permission-step-text {
        font-size: 14px;
        padding-top: 2px;
    }

    .permission-actions {
        display: flex;
        gap: 12px;
        padding: 0 24px 24px;
    }

    .permission-btn {
        flex: 1;
        padding: 12px 20px;
        font-size: 14px;
        font-weight: 500;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
    }

    .permission-btn-primary {
        background: var(--proctor-info);
        color: white;
        border: none;
    }

    .permission-btn-primary:hover {
        filter: brightness(1.1);
    }

    .permission-btn-secondary {
        background: transparent;
        color: var(--proctor-text);
        border: 1px solid var(--proctor-border);
    }

    .permission-btn-secondary:hover {
        background: var(--proctor-bg);
    }

    .permission-error {
        padding: 12px 16px;
        margin: 0 24px 16px;
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-radius: 8px;
        color: var(--proctor-error);
        font-size: 13px;
    }

    /* Position utilities */
    .pos-top-right {
        top: 20px;
        right: 20px;
    }

    .pos-top-left {
        top: 20px;
        left: 20px;
    }

    .pos-bottom-right {
        bottom: 20px;
        right: 20px;
    }

    .pos-bottom-left {
        bottom: 20px;
        left: 20px;
    }

    /* Chat UI */
    .chat-container {
        position: fixed;
        bottom: 180px; /* Above camera preview */
        right: 20px;
        width: 320px;
        height: 400px;
        background: var(--proctor-bg-solid);
        border: 1px solid var(--proctor-border);
        border-radius: 12px;
        box-shadow: var(--proctor-shadow);
        display: flex;
        flex-direction: column;
        z-index: 999996;
        animation: scaleIn 0.2s ease-out;
        pointer-events: auto;
    }

    .chat-container.hidden {
        display: none;
    }

    .chat-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid var(--proctor-border);
        background: var(--proctor-bg);
        border-radius: 12px 12px 0 0;
    }

    .chat-title {
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chat-close-btn {
        background: none;
        border: none;
        color: var(--proctor-text-muted);
        font-size: 20px;
        cursor: pointer;
        padding: 0 4px;
    }

    .chat-close-btn:hover {
        color: var(--proctor-text);
    }

    .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .chat-empty-state {
        text-align: center;
        color: var(--proctor-text-muted);
        font-size: 13px;
        margin-top: 40px;
    }

    .chat-message {
        max-width: 80%;
        padding: 8px 12px;
        border-radius: 12px;
        font-size: 13px;
        position: relative;
        word-wrap: break-word;
    }

    .chat-message.self {
        align-self: flex-end;
        background: var(--proctor-info);
        color: white;
        border-bottom-right-radius: 4px;
    }

    .chat-message.other {
        align-self: flex-start;
        background: var(--proctor-bg);
        border: 1px solid var(--proctor-border);
        border-bottom-left-radius: 4px;
    }

    .message-meta {
        font-size: 10px;
        opacity: 0.7;
        margin-top: 4px;
        text-align: right;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 4px;
    }

    .message-status {
        font-size: 10px;
        color: #888;
    }

    .message-status.delivered {
        color: #4a9eff;
    }

    .message-status.read {
        color: #4caf50;
    }

    .chat-input-area {
        padding: 12px;
        border-top: 1px solid var(--proctor-border);
        display: flex;
        align-items: flex-end;
        gap: 8px;
        background: var(--proctor-bg);
        border-radius: 0 0 12px 12px;
    }

    .chat-input {
        flex: 1;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid var(--proctor-border);
        border-radius: 8px;
        padding: 8px 12px;
        color: var(--proctor-text);
        font-family: inherit;
        font-size: 13px;
        resize: none;
        max-height: 100px;
        outline: none;
    }

    .chat-input:focus {
        border-color: var(--proctor-info);
    }

    .chat-send-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: var(--proctor-info);
        color: white;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: opacity 0.2s;
        flex-shrink: 0;
    }

    .chat-send-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: var(--proctor-text-muted);
    }

    .chat-badge {
        position: absolute;
        top: -5px;
        right: -5px;
        background: var(--proctor-error);
        color: white;
        font-size: 10px;
        font-weight: bold;
        padding: 2px 6px;
        border-radius: 10px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        animation: scaleIn 0.2s ease-out;
        z-index: 999998;
    }
`;

export function getStyles(theme = "dark") {
  const themeVars = themes[theme] || themes.dark;
  const cssVars = Object.entries(themeVars)
    .map(([key, value]) => `${key}: ${value};`)
    .join("\n        ");

  return `
        :host {
            ${cssVars}
        }
        ${baseStyles}
    `;
}

export function applyCustomTheme(customColors) {
  return Object.entries(customColors)
    .map(([key, value]) => `--proctor-${key}: ${value};`)
    .join("\n");
}

export { themes };
